import { usePageTitle } from '../hooks/usePageTitle.js';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, type JiraIssue, type EhrRow, type SessionRow } from '../lib/api.js';

function statusStyle(status: string): { bg: string; color: string; border: string } {
  if (status === 'In Progress') return { bg: 'var(--accent-yellow)22', color: 'var(--accent-yellow)', border: 'var(--accent-yellow)44' };
  if (status === 'Done') return { bg: 'var(--accent-green)22', color: 'var(--accent-green)', border: 'var(--accent-green)44' };
  if (status === 'Blocked') return { bg: 'var(--accent-red)22', color: 'var(--accent-red)', border: 'var(--accent-red)44' };
  return { bg: 'var(--accent-blue)22', color: 'var(--accent-blue)', border: 'var(--accent-blue)44' };
}

function priorityColor(p: string | null): string {
  if (p === 'Highest' || p === 'High') return 'var(--accent-red)';
  if (p === 'Medium') return 'var(--accent-orange)';
  return 'var(--accent-green)';
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function findLinkedSession(sessions: SessionRow[], issueKey: string): SessionRow | undefined {
  const lower = issueKey.toLowerCase();
  return sessions.find(
    (s) => s.title?.toLowerCase().includes(lower) || s.description?.toLowerCase().includes(lower),
  );
}

function findMatchingEhr(ehrs: EhrRow[], clientEmr: string | null): EhrRow | undefined {
  if (!clientEmr) return undefined;
  const lower = clientEmr.toLowerCase();
  return ehrs.find(
    (e) => e.displayName.toLowerCase().includes(lower) || e.slug.toLowerCase().includes(lower),
  );
}

export function TicketDetailPage({ issueKey }: { issueKey: string }) {
  usePageTitle(`Ticket ${issueKey}`);
  const navigate = useNavigate();

  const { data: jiraData, isLoading: jiraLoading, error: jiraError } = useQuery({
    queryKey: ['jira', ''],
    queryFn: () => api.listJiraIssues(),
    retry: false,
  });

  const { data: sessionsData } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.listSessions(),
  });

  const { data: ehrsData } = useQuery({
    queryKey: ['ehrs'],
    queryFn: () => api.listEhrs(),
  });

  const issue: JiraIssue | undefined = jiraData?.issues.find((i) => i.key === issueKey);
  const sessions: SessionRow[] = sessionsData?.sessions ?? [];
  const ehrs: EhrRow[] = ehrsData?.ehrs ?? [];

  const linkedSession = issue ? findLinkedSession(sessions, issue.key) : undefined;
  const matchingEhr = issue ? findMatchingEhr(ehrs, issue.clientEmr) : undefined;

  if (jiraLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>Loading…</div>
      </div>
    );
  }

  if (jiraError || !issue) {
    return (
      <div className="flex h-full flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b px-6" style={{ borderColor: 'var(--border)' }}>
          <button
            type="button"
            onClick={() => void navigate({ to: '/tickets' })}
            className="text-sm"
            style={{ color: 'var(--text-dimmed)' }}
          >
            ← Tickets
          </button>
        </header>
        <div className="flex flex-1 items-center justify-center">
          <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>
            {jiraError instanceof Error ? jiraError.message : `Ticket ${issueKey} not found.`}
          </div>
        </div>
      </div>
    );
  }

  const ss = statusStyle(issue.status);

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b px-6" style={{ borderColor: 'var(--border)' }}>
        <button
          type="button"
          onClick={() => void navigate({ to: '/tickets' })}
          className="text-sm transition-colors"
          style={{ color: 'var(--text-dimmed)' }}
        >
          ← Tickets
        </button>
        <span style={{ color: 'var(--text-dimmed)' }}>/</span>
        <span className="font-mono text-sm" style={{ color: 'var(--text-secondary)' }}>{issue.key}</span>
      </header>

      <section className="flex-1 overflow-y-auto p-6">
        <div className="flex gap-6 max-w-5xl">
          <div className="flex-1 min-w-0 space-y-6">
            <div
              className="rounded-2xl p-6 space-y-5"
              style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
            >
              <div>
                <div className="font-mono text-xs mb-2" style={{ color: 'var(--text-dimmed)' }}>{issue.key}</div>
                <h1 className="text-2xl font-bold leading-snug" style={{ color: 'var(--text-primary)' }}>
                  {issue.summary}
                </h1>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <span
                  className="rounded-full px-3 py-1 text-sm font-medium"
                  style={{ background: ss.bg, color: ss.color, border: `1px solid ${ss.border}` }}
                >
                  {issue.status}
                </span>
                {issue.clientEmr && (
                  <span
                    className="rounded px-2 py-0.5 text-sm"
                    style={{ background: '#06b6d422', color: '#06b6d4', border: '1px solid #06b6d433' }}
                  >
                    {issue.clientEmr}
                  </span>
                )}
                {issue.priority && (
                  <span className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: priorityColor(issue.priority) }} />
                    {issue.priority}
                  </span>
                )}
                {issue.assignee && (
                  <span className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <span
                      className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white"
                      style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
                    >
                      {issue.assignee[0]?.toUpperCase()}
                    </span>
                    {issue.assignee}
                  </span>
                )}
                {issue.goLiveDate && (
                  <span className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--accent-yellow)' }}>
                    🗓 Go-live {formatDate(issue.goLiveDate)}
                  </span>
                )}
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-dimmed)' }}>
                  Actions
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div
                    className="rounded-xl p-4 space-y-3"
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}
                  >
                    <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Import Session</div>
                    {linkedSession ? (
                      <>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Linked to <span style={{ color: 'var(--accent-blue)' }}>{linkedSession.title ?? linkedSession.id.slice(0, 8)}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => void navigate({ to: '/sessions/$id', params: { id: linkedSession.id } })}
                          className="rounded-xl px-3 py-1.5 text-xs font-semibold"
                          style={{ background: 'var(--accent-blue)22', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)44' }}
                        >
                          View Session →
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>No session linked yet.</div>
                        <button
                          type="button"
                          onClick={() => void navigate({ to: '/' })}
                          className="rounded-xl px-3 py-1.5 text-xs font-semibold text-white"
                          style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
                        >
                          + New Import Session
                        </button>
                      </>
                    )}
                  </div>

                  <div
                    className="rounded-xl p-4 space-y-3"
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}
                  >
                    <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>EHR Source</div>
                    {issue.clientEmr ? (
                      <>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Client EMR: <span style={{ color: '#06b6d4' }}>{issue.clientEmr}</span>
                        </div>
                        {matchingEhr ? (
                          <button
                            type="button"
                            onClick={() => void navigate({ to: '/ehrs/$id', params: { id: matchingEhr.id } })}
                            className="rounded-xl px-3 py-1.5 text-xs font-semibold"
                            style={{ background: 'var(--accent-blue)22', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)44' }}
                          >
                            View {matchingEhr.displayName} →
                          </button>
                        ) : (
                          <div className="text-xs" style={{ color: 'var(--text-dimmed)' }}>No matching EHR configured.</div>
                        )}
                      </>
                    ) : (
                      <div className="text-xs" style={{ color: 'var(--text-dimmed)' }}>No EMR detected.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-72 shrink-0 space-y-4">
            <div
              className="rounded-2xl p-4 space-y-3"
              style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
            >
              <div className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-dimmed)' }}>
                Jira Details
              </div>
              {(
                [
                  ['Status', <span key="s" className="rounded-full px-2 py-0.5 text-xs font-medium" style={{ background: ss.bg, color: ss.color, border: `1px solid ${ss.border}` }}>{issue.status}</span>],
                  ['Priority', issue.priority ? <span key="p" className="flex items-center gap-1.5 text-sm"><span className="h-2 w-2 rounded-full" style={{ background: priorityColor(issue.priority) }} />{issue.priority}</span> : '—'],
                  ['Assignee', issue.assignee ?? '—'],
                  ['Client EMR', issue.clientEmr ?? '—'],
                  ['Go-live', issue.goLiveDate ? formatDate(issue.goLiveDate) : '—'],
                ] as [string, React.ReactNode][]
              ).map(([label, value]) => (
                <div key={String(label)} className="flex items-center justify-between gap-2">
                  <span className="text-xs shrink-0" style={{ color: 'var(--text-dimmed)' }}>{label}</span>
                  <span className="text-xs text-right" style={{ color: 'var(--text-secondary)' }}>{value}</span>
                </div>
              ))}
              {issue.customerUrl && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs shrink-0" style={{ color: 'var(--text-dimmed)' }}>Customer URL</span>
                  <a
                    href={issue.customerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs truncate max-w-[160px] hover:underline"
                    style={{ color: 'var(--text-dimmed)' }}
                  >
                    {issue.customerUrl.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
            </div>

            <a
              href={issue.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold"
              style={{ background: 'var(--bg-tertiary)', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)33' }}
            >
              ↗ Open in Jira
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
